import React, { useEffect, useMemo, useRef, useState } from "react"
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap"
import { InputSwitch } from "primereact/inputswitch"
import Swal from "sweetalert2"
import axios from "axios"

const DEFAULTS = {
  resume_scoring_enabled: false,
  automatic_screening_invites_enabled: false,
  calling_agent_enabled: false,
  resume_invite_threshold: 50,
  manual_talent_scan_delivery_mode: "calendnow",
}
const ACCESS_ERROR =
  "Only SuperAdmin or an authorized company administrator can edit automation settings."
const asBoolean = value =>
  value === true || value === 1 || String(value).toLowerCase() === "true"

export default function CompanyAutomationSettings() {
  useEffect(() => {
    const previousTitle = document.title
    document.title = "Company Automation Settings | Bharat ATS"
    return () => {
      document.title = previousTitle
    }
  }, [])
  const [companies, setCompanies] = useState([])
  const [company, setCompany] = useState(null)
  const [settings, setSettings] = useState(DEFAULTS)
  const [original, setOriginal] = useState(DEFAULTS)
  const [loadingCompanies, setLoadingCompanies] = useState(true)
  const [loadingSettings, setLoadingSettings] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [dropdownQuery, setDropdownQuery] = useState("")
  const dropdownRef = useRef(null)
  const user = useMemo(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem("user-info") || "{}")
      return stored.user_details || stored
    } catch (parseError) {
      return {}
    }
  }, [])
  const companyId = company?.id || company?.company_id

  useEffect(() => {
    const closeOnOutsideClick = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", closeOnOutsideClick)
    return () => document.removeEventListener("mousedown", closeOnOutsideClick)
  }, [])

  useEffect(() => {
    const role = String(user.role_name || user.role || user.user_type || "")
      .toLowerCase()
      .replace(/[_-]/g, " ")
    setIsSuperAdmin(
      Boolean(
        user.is_superuser ||
          user.is_super_admin ||
          user.is_superadmin ||
          ["superadmin", "super admin"].includes(role)
      )
    )
  }, [user])
  useEffect(() => {
    let active = true
    axios
      .get("/api/v1/company/?size=1000")
      .then(({ data }) => {
        if (!active) return
        const rows = Array.isArray(data)
          ? data
          : data.results || data.data || []
        const activeRows = rows.filter(
          item =>
            item.is_active !== false &&
            item.active !== false &&
            item.status !== "inactive"
        )
        setCompanies(activeRows)
        if (activeRows.length === 1) setCompany(activeRows[0])
      })
      .catch(
        e =>
          active &&
          setError(
            e.response?.status === 403
              ? ACCESS_ERROR
              : "Unable to load companies."
          )
      )
      .finally(() => active && setLoadingCompanies(false))
    return () => {
      active = false
    }
  }, [])
  useEffect(() => {
    if (!companyId) return undefined
    let active = true
    setLoadingSettings(true)
    setError("")
    axios
      .get(`/api/v1/company/${companyId}/`)
      .then(({ data }) => {
        if (!active) return
        const loaded = {
          resume_scoring_enabled: asBoolean(data.resume_scoring_enabled),
          automatic_screening_invites_enabled: asBoolean(
            data.automatic_screening_invites_enabled
          ),
          calling_agent_enabled: asBoolean(data.calling_agent_enabled),
          resume_invite_threshold:
            data.resume_invite_threshold == null
              ? 50
              : Number(data.resume_invite_threshold),
          manual_talent_scan_delivery_mode:
            data.manual_talent_scan_delivery_mode || "calendnow",
        }
        setSettings(loaded)
        setOriginal(loaded)
      })
      .catch(
        e =>
          active &&
          setError(
            [403, 404].includes(e.response?.status)
              ? ACCESS_ERROR
              : "Unable to load company automation settings. Please try again."
          )
      )
      .finally(() => active && setLoadingSettings(false))
    return () => {
      active = false
    }
  }, [companyId])
  const update = (field, value) =>
    setSettings(current => ({ ...current, [field]: value }))
  const optionLabel = item => {
    const name = item?.company_name || item?.name
    const domain = item?.company_domain
    if (!name && !domain) return "--"
    if (!name) return domain
    if (!domain) return name
    return `${name} - ${domain}`
  }
  const renderOptionLabel = item => {
    const name = item?.company_name || item?.name
    const domain = item?.company_domain
    if (!name && !domain) return "--"
    if (!name) return <strong>{domain}</strong>
    if (!domain) return name
    return <>{name} - <strong>{domain}</strong></>
  }
  const dropdownOptions = companies.filter(item =>
    optionLabel(item).toLowerCase().includes(dropdownQuery.toLowerCase())
  )
  const save = async () => {
    const threshold = Number(settings.resume_invite_threshold)
    if (!Number.isInteger(threshold) || threshold < 0 || threshold > 100) {
      setError(
        "Resume invitation threshold must be a whole number from 0 to 100."
      )
      return
    }
    const payload = {}
    Object.keys(DEFAULTS).forEach(field => {
      if (settings[field] !== original[field])
        payload[field] =
          field === "resume_invite_threshold"
            ? threshold
            : field === "manual_talent_scan_delivery_mode"
            ? settings[field] || "calendnow"
            : Boolean(settings[field])
    })
    if (!Object.keys(payload).length) return
    setSaving(true)
    setError("")
    try {
      await axios.patch(`/api/v1/company/${companyId}/`, payload)
      const { data } = await axios.get(`/api/v1/company/${companyId}/`)
      const loaded = {
        resume_scoring_enabled: asBoolean(data.resume_scoring_enabled),
        automatic_screening_invites_enabled: asBoolean(
          data.automatic_screening_invites_enabled
        ),
        calling_agent_enabled: asBoolean(data.calling_agent_enabled),
        resume_invite_threshold:
          data.resume_invite_threshold == null
            ? 50
            : Number(data.resume_invite_threshold),
        manual_talent_scan_delivery_mode:
          data.manual_talent_scan_delivery_mode || "calendnow",
      }
      setSettings(loaded)
      setOriginal(loaded)
      await Swal.fire(
        "Saved",
        "Company automation settings were updated successfully.",
        "success"
      )
    } catch (e) {
      const status = e.response?.status
      setError(
        status === 403
          ? ACCESS_ERROR
          : status === 404
          ? "Company was not found."
          : e.response?.data?.detail ||
            e.response?.data?.error ||
            "Unable to update company automation settings."
      )
    } finally {
      setSaving(false)
    }
  }
  return (
    <div className="page-content">
      <Container fluid>
        <div className="page-title-box d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h1 className="page-title mb-1">Company Automation Settings</h1>
            <p className="text-muted mb-0">
              Configure automation for an active company.
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div ref={dropdownRef} className="position-relative" style={{ width: 360 }}>
              <input type="text" className="form-control" style={{ paddingRight: dropdownQuery ? 38 : undefined }} value={dropdownQuery} onFocus={() => setDropdownOpen(true)} onChange={event => { setDropdownQuery(event.target.value); setDropdownOpen(true); }} placeholder={loadingCompanies ? "Loading companies..." : "Search companies..."} disabled={loadingCompanies || saving} />
              {dropdownQuery && <button type="button" aria-label="Clear company search" onClick={() => { setDropdownQuery(""); setCompany(null); setDropdownOpen(true); }} disabled={saving} style={{ position: "absolute", right: 8, top: 0, bottom: 0, border: 0, background: "transparent", color: "#6c757d", fontSize: 22, lineHeight: 1, padding: "0 4px", display: "flex", alignItems: "center" }}>×</button>}
              {dropdownOpen && <div className="position-absolute bg-white border rounded shadow-sm w-100 mt-2" style={{ zIndex: 30, minWidth: 360, overflowX: "hidden", backgroundColor: "#fff" }}>
                <div style={{ maxHeight: 280, overflowY: "auto", overflowX: "hidden", padding: "4px 0" }}>{dropdownOptions.map(item => <button type="button" key={item.id || item.company_id} className="dropdown-item text-start" style={{ whiteSpace: "normal", overflowWrap: "anywhere", width: "100%", padding: "13px 16px", minHeight: "48px", lineHeight: 1.45, borderBottom: "1px solid #eef1f5", color: "#243447", backgroundColor: "#fff" }} onClick={() => { setCompany(item); setDropdownQuery(optionLabel(item)); setDropdownOpen(false); }}>{renderOptionLabel(item)}</button>)}{!dropdownOptions.length && <div className="text-muted small p-3">No companies found.</div>}</div>
              </div>}
            </div>
            <Button color="primary" onClick={save} disabled={!company || loadingSettings || saving}>{saving ? "Saving..." : "Save settings"}</Button>
          </div>
        </div>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <Card className="bg-form">
          <CardBody>
            <div className="alert alert-info">
              You can manage settings for any active company.
            </div>
            {loadingSettings && (
              <div className="text-muted mb-3">
                Loading selected company settings...
              </div>
            )}
            <Row className="g-3">
              {[
                [
                  "resume_scoring_enabled",
                  "Resume Scoring Enabled",
                  "Allows resume scoring to run.",
                ],
                [
                  "automatic_screening_invites_enabled",
                  "Automatic Screening Invitations",
                  "Sends invitations after a qualifying score.",
                ],
                [
                  "calling_agent_enabled",
                  "Calling Agent Enabled",
                  "Allows scheduled Vitel calls to be triggered.",
                ],
              ].map(([field, label, help]) => (
                <Col lg={4} key={field}>
                  <div className="border rounded p-3 h-100">
                    <div className="d-flex justify-content-between align-items-center gap-2">
                      <label className="fw-semibold mb-0" htmlFor={field}>
                        {label}
                      </label>
                      <InputSwitch
                        inputId={field}
                        checked={settings[field]}
                        onChange={event => update(field, event.value)}
                        disabled={!company || loadingSettings || saving}
                      />
                    </div>
                    <small className="text-muted d-block mt-2">{help}</small>
                  </div>
                </Col>
              ))}
              <Col lg={4}>
                <div className="border rounded p-3">
                  <label
                    className="fw-semibold"
                    htmlFor="resume_invite_threshold"
                  >
                    Resume Invitation Threshold
                  </label>
                  <input
                    id="resume_invite_threshold"
                    type="number"
                    min="0"
                    max="100"
                    className="form-control mt-2"
                    value={settings.resume_invite_threshold}
                    onKeyDown={event => {
                      if (event.key === "-" || event.key === "e" || event.key === "E") {
                        event.preventDefault()
                      }
                    }}
                    onChange={event => {
                      const value = event.target.value
                      update("resume_invite_threshold", value === "" ? 0 : Math.max(0, Number(value)))
                    }}
                    disabled={!company || loadingSettings || saving}
                  />
                </div>
              </Col>
              <Col lg={6}>
                <div className="border rounded p-3 h-100">
                  <label className="fw-semibold" htmlFor="manual_talent_scan_delivery_mode">
                    Manual TalentScan Delivery
                  </label>
                  <select
                    id="manual_talent_scan_delivery_mode"
                    className="form-select mt-2"
                    value={settings.manual_talent_scan_delivery_mode || "calendnow"}
                    onChange={event => update("manual_talent_scan_delivery_mode", event.target.value)}
                    disabled={!company || loadingSettings || saving}
                  >
                    <option value="direct">Direct TalentScan</option>
                    <option value="calendnow">CalendNow Booking</option>
                  </select>
                  <small className="text-muted d-block mt-2">
                    Choose whether manual TalentScan delivery is immediate or booking-based.
                  </small>
                </div>
              </Col>
            </Row>
            {settings.manual_talent_scan_delivery_mode === "direct" ? (
              <div className="alert alert-warning mt-3 mb-0">
                Manual TalentScan tests will be sent immediately without asking the candidate to book a CalendNow slot.
              </div>
            ) : (
              <div className="alert alert-info mt-3 mb-0">
                Candidates will receive a CalendNow link before the TalentScan test is assigned.
              </div>
            )}
            {settings.automatic_screening_invites_enabled &&
              !settings.resume_scoring_enabled && (
                <div className="alert alert-warning mt-3 mb-0">
                  Automatic invitations require resume scoring to be enabled.
                </div>
              )}
            {!settings.calling_agent_enabled && company && (
              <div className="alert alert-warning mt-3 mb-0">
                Candidates may book interviews, but Vitel calls will remain
                paused.
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}
